import React, { useRef, useState } from 'react';
import { firePinBurst, PIN_PULSE_MS } from './pinBurst';

// Bare-star pin toggle used in list rows (Suppliers, Briefs). Header
// pill buttons on the supplier/brief detail pages don't use this
// component - they keep their own pill layout and call firePinBurst()
// directly. Both paths share the same CSS keyframes + particle SVG so
// the animation is identical across surfaces.
//
// Pin fires the full pulse + 5-star burst. Unpin is a quieter
// colour-only transition - the spec is explicit that undoing an action
// doesn't warrant the celebration.

const STAR_POINTS = '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2';

export default function PinButton({
  isPinned,
  onToggle,
  size = 14,
  label,            // e.g. 'supplier' / 'brief' — used in the aria label
  className = '',
}) {
  const btnRef = useRef(null);
  const [pulsing, setPulsing] = useState(false);

  const handleClick = (e) => {
    // Pin buttons live inside clickable rows (Suppliers, Briefs lists)
    // that navigate on row click. Stopping propagation here means the
    // call sites don't have to pass the event into their toggle fn.
    e.stopPropagation();
    if (!isPinned) {
      setPulsing(true);
      firePinBurst(btnRef.current);
      setTimeout(() => setPulsing(false), PIN_PULSE_MS);
    }
    onToggle?.();
  };

  const verb = isPinned ? 'Unpin' : 'Pin';
  const fullLabel = label ? `${verb} ${label}` : verb;

  return (
    <button
      ref={btnRef}
      type="button"
      onClick={handleClick}
      title={fullLabel}
      aria-pressed={isPinned}
      aria-label={fullLabel}
      className={`pin-toggle p-1 ${isPinned ? 'pin-toggle--pinned' : ''} ${className}`}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={isPinned ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className={pulsing ? 'pin-star--pulsing' : ''}>
        <polygon points={STAR_POINTS} />
      </svg>
    </button>
  );
}
